import { useCallback } from 'react'
import { useDispatch, useStore } from 'react-redux'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { useTenant } from '@emerald-works-nova/auth'
import consts from '../consts'
import { subscriptionSlice, userSlice } from '../reducers'

/**
 * The one way in to `subscriptionGetAccountInfo`, and the one place that
 * decides whether to send it:
 *
 * - only the Club tenant sells subscriptions, so nobody else pays for the
 *   request
 * - not before the profile lands: the request carries the username
 * - not while one is already out
 *
 * Three components ask for account info and they all mount together: the paid
 * toolbar, the Account settings layout and PaymentProfile (each through
 * `useEnsureAccountInfo`). Reading the in-flight status through `useSelector`
 * let all three through in the same commit — each effect sees the value from
 * its own render, which is still idle — so it is read off the store here,
 * after whichever of them dispatched first. The retry paths lean on the same
 * check: the automatic one, the "Try again" button and a refetch after a plan
 * change can all fire at once without two responses racing into the store.
 */
const useAccountInfoRequest = () => {
  const store = useStore()
  const dispatch = useDispatch()
  const tenant = useTenant()
  const isClubTenant = tenant.pk === consts.DEFAULT_CLUB_PK

  const [subscriptionGetAccountInfo] = useEvent([
    subscriptionSlice.eventBus.subscriptionGetAccountInfo
  ])

  return useCallback(({ invalidateCache = false } = {}) => {
    if (!isClubTenant) return

    const state = store.getState()
    if (subscriptionSlice.selectors.selectAccountInfoRequested(state)) return

    const profile = userSlice.selectors.selectProfile(state)
    if (!profile?.username) return

    dispatch(subscriptionSlice.actions.setAccountInfoRequested())
    subscriptionGetAccountInfo.trigger({
      accountId: profile.username,
      invalidateCache
    })
  }, [isClubTenant, store, dispatch, subscriptionGetAccountInfo])
}

export default useAccountInfoRequest
